import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { LedgerGlyph, type GlyphKind } from './glyph';

/* ---------------------------------------------------------------------------
   The Run Ledger rail.

   A hairline vertical rule down the thread. Each stop is a glyph centred on it;
   between stops the rail carries progress notches — short ticks that encode the
   phases a run passes through. The geometry is fixed and duplicated verbatim
   across the three dashboards, because with no shared package this device is the
   only thing that makes them read as one product:

       rail x        = 13px from the container's left edge
       glyph box     = 11 × 11, centred on the rail
       notch tick    = 5px, drawn to the right of the rail
       content inset = 28px

   Everything else about a ledger — what a stop is, what the notches are called —
   varies per surface. The rail does not.
--------------------------------------------------------------------------- */

export function LedgerRail({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('relative', className)}>
      {/* The rail itself. It stops short at the top and bottom so a ledger does
          not appear to continue past its first and last stop. */}
      <div className="pointer-events-none absolute bottom-2 left-[13px] top-2 w-px bg-rule" aria-hidden />
      <ol className="relative m-0 list-none p-0">{children}</ol>
    </div>
  );
}

export interface LedgerStopProps {
  glyph: GlyphKind;
  title: ReactNode;
  /** Mono, right-aligned: the elapsed time or the clock reading for this stop. */
  timing?: ReactNode;
  /** One line under the title. */
  subtitle?: ReactNode;
  children?: ReactNode;
  /** Draws the stop as the one currently in progress. */
  current?: boolean;
  className?: string;
}

export function LedgerStop({ glyph, title, timing, subtitle, children, current, className }: LedgerStopProps) {
  return (
    <li className={cn('relative pb-3 pl-7 last:pb-0', className)}>
      <span className="absolute left-[2.5px] top-[3px] block">
        <LedgerGlyph kind={glyph} />
      </span>

      <div className="flex items-baseline justify-between gap-3">
        <div className={cn('min-w-0 text-sm', current ? 'font-medium text-ink' : 'text-ink')}>{title}</div>
        {timing ? <div className="shrink-0 font-mono text-tiny tabular-nums text-ink-3">{timing}</div> : null}
      </div>

      {subtitle ? <div className="mt-0.5 text-xs leading-[16px] text-ink-2">{subtitle}</div> : null}
      {children ? <div className="mt-1.5">{children}</div> : null}
    </li>
  );
}

export type NotchState = 'passed' | 'current' | 'future';

export interface Notch {
  key: string;
  label: string;
  state: NotchState;
  /** Mono timing for a notch the console actually observed being reached. */
  timing?: string;
}

/** The progress notches between two stops. This is the part of the device that
    carries information rather than decoration: each tick is a real phase of the
    contract's server-derived progress projection, and the rail shows how far
    along it the run got — including, on a failure, exactly where it stopped. */
export function LedgerNotches({ notches, className }: { notches: Notch[]; className?: string }) {
  return (
    <ul className={cn('m-0 list-none p-0', className)}>
      {notches.map((notch) => (
        <li key={notch.key} className="relative flex items-center gap-2 py-[1px]">
          {/* -15px places the tick on the rail: the stop content is inset 28px,
              and the rail sits at 13px. */}
          <span
            aria-hidden
            className={cn(
              'absolute left-[-15px] block h-px w-[5px]',
              notch.state === 'passed' && 'bg-ink-3',
              notch.state === 'current' && 'h-[2px] animate-notch-pulse bg-action',
              notch.state === 'future' && 'bg-rule',
            )}
          />
          <span
            className={cn(
              'font-mono text-tiny',
              notch.state === 'passed' && 'text-ink-2',
              notch.state === 'current' && 'text-live',
              notch.state === 'future' && 'text-ink-3 opacity-60',
            )}
          >
            {notch.label}
          </span>
          {notch.timing ? (
            <span className="font-mono text-tiny tabular-nums text-ink-3">{notch.timing}</span>
          ) : null}
        </li>
      ))}
    </ul>
  );
}
